/**
 * @module animations/reveal
 *
 * Generic scroll-reveal behaviour for any element marked `data-reveal`.
 *
 * All of the "cannot strand content" logic lives in
 * {@link module:animations/scrollReveal}: targets that are already inside the
 * viewport when the page loads — which is every target on a deep link — are
 * animated immediately, rather than waiting for a scroll event that will never
 * come. A `ScrollTrigger` that is already past its `start` at creation never
 * fires `onEnter`, and the old implementation left those elements invisible.
 */

import { gsap, EASE, MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qsa } from "../core/dom.js";
import { watchReveal } from "./scrollReveal.js";

/**
 * @typedef {Object} RevealOptions
 * @param {string} [selector="[data-reveal]"]
 * @param {number} [y=22]        Vertical offset before reveal.
 * @param {number} [duration=0.9]
 * @param {number} [stagger=0.08]
 */

/**
 * Initialise scroll reveals.
 *
 * @param {RevealOptions} [options]
 * @returns {() => void} Teardown.
 */
export function initReveals(options = {}) {
  const {
    selector = "[data-reveal]",
    y = 22,
    duration = 0.9,
    stagger = 0.08,
  } = options;

  const elements = qsa(selector);
  if (!elements.length) return () => {};

  const mm = createMotionContext();
  /** @type {() => void} */
  let disposeReveal = () => {};

  // --- Reduced motion: make everything visible, no transform, no trigger. ---
  mm.add(MEDIA.reduce, () => {
    gsap.set(elements, { clearProps: "all", autoAlpha: 1 });
  });

  // --- Expressive motion ---------------------------------------------------
  mm.add(MEDIA.any, () => {
    disposeReveal = watchReveal(elements, {
      from: { autoAlpha: 0, y },
      to: { autoAlpha: 1, y: 0 },
      duration,
      ease: EASE.out,
      stagger,
      start: "top 88%",
    });

    return () => {
      disposeReveal();
      disposeReveal = () => {};
      // matchMedia reverts tweens; make sure nothing is left invisible.
      gsap.set(elements, { clearProps: "all" });
    };
  });

  return () => {
    disposeReveal();
    mm.revert();
  };
}

/**
 * Animate a single group of elements immediately (no scroll trigger). Useful
 * for above-the-fold content such as the hero.
 *
 * @param {string|Element|Element[]} targets
 * @param {Object} [options]
 * @param {number} [options.y=24]
 * @param {number} [options.duration=1]
 * @param {number} [options.stagger=0.1]
 * @param {number} [options.delay=0]
 * @returns {gsap.core.Tween|null} The tween, or `null` under reduced motion.
 */
export function revealNow(targets, options = {}) {
  const { y = 24, duration = 1, stagger = 0.1, delay = 0 } = options;

  const mm = createMotionContext();
  let tween = null;

  mm.add(MEDIA.reduce, () => {
    gsap.set(targets, { clearProps: "all", autoAlpha: 1 });
  });

  mm.add(MEDIA.any, () => {
    gsap.set(targets, { autoAlpha: 0, y });
    tween = gsap.to(targets, {
      autoAlpha: 1,
      y: 0,
      duration,
      delay,
      stagger,
      ease: EASE.out,
      onComplete: () => gsap.set(targets, { willChange: "auto" }),
    });
    return tween;
  });

  // The teardown is attached to the tween so callers can stop it if needed.
  if (tween) {
    const original = tween.kill.bind(tween);
    tween.kill = (...args) => {
      mm.revert();
      return original(...args);
    };
  }

  return tween;
}
